import { AutoComplete } from "primereact/autocomplete";
import { BlockUI } from "primereact/blockui";
import { ProgressSpinner } from "primereact/progressspinner";
import { useEffect, useRef, useState } from "react";

export default function ListaDesplegable({
  Lista,
  setValor,
  Seleccionado,
  TituloNoSeleccionado,
  Buscando,
  SoloLectura,
  MostrarEstado = true,
  BuscarRemoto,
  ActualizarSoloAlSeleccionar = false,
  Filtrar,
  ItemTemplate,
  MensajeVacio = "No hay resultados",
  Error,
  Reintentar,
  Deshabilitado = false,
  Placeholder,
  MinCaracteresBusqueda = 1,
  ConfirmarTexto,
}) {
  const [Filtrado, setFiltrado] = useState(null);
  const [ValorEntrada, setValorEntrada] = useState(Seleccionado);
  const ultimaConsulta = useRef("");
  const seleccionLimpiadaAlEscribir = useRef(false);
  const valorEntradaActual = useRef(Seleccionado);
  const confirmandoTexto = useRef(false);
  const temporizadorPerderFoco = useRef(null);

  useEffect(() => {
    if (ActualizarSoloAlSeleccionar) {
      if (
        Seleccionado == null &&
        seleccionLimpiadaAlEscribir.current &&
        typeof valorEntradaActual.current === "string"
      ) return;
      setValorEntrada(Seleccionado);
      valorEntradaActual.current = Seleccionado;
    }
  }, [Seleccionado, ActualizarSoloAlSeleccionar]);

  useEffect(() => () => window.clearTimeout(temporizadorPerderFoco.current), []);

  useEffect(() => {
    if (BuscarRemoto) {
      setFiltrado(Array.isArray(Lista) ? [...Lista] : []);
    }
  }, [Lista, BuscarRemoto]);

  const search = async (event) => {
    const query = String(event.query ?? "").trim();
    const listaBase = Array.isArray(Lista) ? Lista : [];

    if (BuscarRemoto) {
      ultimaConsulta.current = query;
      const resultado = await BuscarRemoto(query);
      if (ultimaConsulta.current === query && Array.isArray(resultado)) {
        setFiltrado(resultado);
      }
      return;
    }

    if (Filtrar) {
      setFiltrado(Filtrar(listaBase, query));
      return;
    }

    const queryNormalizada = query.toLowerCase();
    setFiltrado(
      queryNormalizada
        ? [...listaBase.filter((item) => {
            const descripcion = String(item?.sDescripcion ?? "").toLowerCase();
            const codigo = String(item?.sCodigo ?? "").toLowerCase();
            return descripcion.includes(queryNormalizada) || codigo.includes(queryNormalizada);
          })]
        : [...listaBase]
    );
  };

  const onCambio = (e) => {
    if (ActualizarSoloAlSeleccionar) {
      setValorEntrada(e.value);
      valorEntradaActual.current = e.value;
      const esTexto = typeof e.value === "string";
      const alcanzoMinimo = esTexto && e.value.trim().length >= MinCaracteresBusqueda;
      const debeLimpiarSeleccion = e.value === null || alcanzoMinimo;

      if (debeLimpiarSeleccion && Seleccionado && !seleccionLimpiadaAlEscribir.current) {
        seleccionLimpiadaAlEscribir.current = true;
        setValor(null);
      }
      return;
    }
    setValor(e.value);
  };

  const onSeleccion = (e) => {
    if (!ActualizarSoloAlSeleccionar) return;
    seleccionLimpiadaAlEscribir.current = false;
    setValorEntrada(e.value);
    valorEntradaActual.current = e.value;
    setValor(e.value);
  };

  const confirmarValorEscrito = async (textoIngresado) => {
    const texto = String(textoIngresado ?? "").trim();
    if (!ConfirmarTexto || texto.length < MinCaracteresBusqueda || confirmandoTexto.current) return;

    const normalizado = texto.toLocaleLowerCase();
    const existente = (Array.isArray(Lista) ? Lista : []).find((item) =>
      [item?.sDescripcion, item?.sCodigo]
        .some((valor) => String(valor ?? "").trim().toLocaleLowerCase() === normalizado)
    );

    if (existente) {
      seleccionLimpiadaAlEscribir.current = false;
      valorEntradaActual.current = existente;
      setValorEntrada(existente);
      setValor(existente);
      return;
    }

    confirmandoTexto.current = true;
    try {
      const confirmado = await ConfirmarTexto(texto);
      if (confirmado && typeof confirmado === "object") {
        seleccionLimpiadaAlEscribir.current = false;
        valorEntradaActual.current = confirmado;
        setValorEntrada(confirmado);
      }
    } finally {
      confirmandoTexto.current = false;
    }
  };

  const onPerderFoco = () => {
    if (!ActualizarSoloAlSeleccionar || typeof valorEntradaActual.current !== "string") return;
    window.clearTimeout(temporizadorPerderFoco.current);
    temporizadorPerderFoco.current = window.setTimeout(() => {
      const valorActual = valorEntradaActual.current;
      if (typeof valorActual !== "string") return;
      if (ConfirmarTexto && valorActual.trim().length >= MinCaracteresBusqueda) {
        confirmarValorEscrito(valorActual);
        return;
      }
      valorEntradaActual.current = Seleccionado;
      setValorEntrada(Seleccionado);
    }, 150);
  };

  const onTecla = async (event) => {
    if (
      event.key !== "Enter" ||
      !ConfirmarTexto ||
      !ActualizarSoloAlSeleccionar ||
      typeof valorEntradaActual.current !== "string"
    ) return;

    const texto = valorEntradaActual.current.trim();
    if (texto.length < MinCaracteresBusqueda) return;

    event.preventDefault();
    await confirmarValorEscrito(texto);
  };

  const handleFocus = (event) => {
    event.target.dispatchEvent(new Event("input", { bubbles: true }));
  };

  return (
    <BlockUI
      blocked={Buscando}
      style={{ borderRadius: "10px", overflow: "hidden" }}
      template={<ProgressSpinner style={{ width: "24px", height: "24px", color: "white" }} strokeWidth="8" animationDuration="5.15s" />}
    >
      <AutoComplete
        className="lista-desplegable"
        style={{ width: "100%" }}
        value={ActualizarSoloAlSeleccionar ? ValorEntrada : Seleccionado}
        suggestions={!SoloLectura ? Filtrado : []}
        completeMethod={search}
        minLength={MinCaracteresBusqueda}
        delay={BuscarRemoto ? 350 : 300}
        onChange={onCambio}
        onSelect={onSeleccion}
        onKeyDown={onTecla}
        onBlur={onPerderFoco}
        inputStyle={{ backgroundColor: "white", width: "100%", color: "black", fontSize: "14px", fontWeight: "bold" }}
        field="sDescripcion"
        readOnly={SoloLectura}
        disabled={Deshabilitado}
        placeholder={Placeholder}
        panelClassName="lista-desplegable-panel"
        panelStyle={{ color: "black", fontSize: "14px" }}
        onFocus={handleFocus}
        itemTemplate={ItemTemplate}
        emptyMessage={MensajeVacio}
      />

      {Error && (
        <div className="lista-desplegable-error" role="alert">
          <span>{Error}</span>
          {Reintentar && <button type="button" onClick={Reintentar}>Reintentar</button>}
        </div>
      )}
      {MostrarEstado && (
        <div style={{ padding: "2px", textAlign: "center" }}>
          {Seleccionado && Seleccionado.sDescripcion ? (
            <span style={{ color: "green", fontSize: "10px" }}>{Seleccionado.sDescripcion} Seleccionado.</span>
          ) : (
            <span style={{ color: "red", fontSize: "10px" }}>
              {Seleccionado && TituloNoSeleccionado}
            </span>
          )}
        </div>
      )}
    </BlockUI>
  );
}
